import { Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Loading, ModuleHero, Notice, StatusPill } from '../../components/ui';
import NoCompany from '../../components/NoCompany';
import { CARD, CARD_STYLE } from '../../lib/ui';

const STATUS: Record<string, { label: string; tone: 'pink' | 'green' | 'amber' | 'slate' | 'blue' | 'rose' }> = {
  SENT: { label: 'Engaged', tone: 'blue' },
  UNDER_REVIEW: { label: 'Under review', tone: 'blue' },
  INFO_REQUESTED: { label: 'Info requested', tone: 'amber' },
  OFFERED: { label: 'Offer received', tone: 'pink' },
  ACCEPTED: { label: 'Offer accepted', tone: 'green' },
  DISBURSED: { label: 'Disbursed', tone: 'green' },
  DECLINED: { label: 'Declined', tone: 'rose' },
  WITHDRAWN: { label: 'Withdrawn', tone: 'slate' },
};

type Submission = {
  id: string;
  createdAt: string;
  firm: string;
  focus: string | null;
  status: string;
  statusUpdatedAt: string | null;
  note?: string | null;
  offerAmount?: string | null;
};

type Response = {
  companyStatus: string;
  fundingAmount: string | null;
  submissions: Submission[];
};

function formatWhen(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Customer view: partner status + when it was done (updates come from Consult). */
export default function Partners() {
  const { data, loading, error, code } = useApi<Response>('/customer/partners');

  if (loading) return <Loading />;
  if (code === 'NO_COMPANY') return <NoCompany />;
  if (error || !data) return <Notice tone="rose">{error}</Notice>;

  const rows = data.submissions || [];
  const outreach = ['READY', 'OUTREACH', 'FUNDED'].includes(data.companyStatus);

  return (
    <div>
      <ModuleHero
        eyebrow="Step 4 · Partners"
        title="Partner outreach"
        description="Your Micus analyst emails funding partners and posts status updates here. Everyday chat stays on Messages."
        actions={data.fundingAmount ? <StatusPill label={`Ask: ${data.fundingAmount}`} tone="pink" /> : undefined}
      />

      {!outreach ? (
        <div className="mt-8">
          <Notice tone="amber">
            Partner outreach starts after you approve the Ready pack.{' '}
            <Link to="/ready" className="font-bold underline">
              Open Ready
            </Link>
          </Notice>
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-8">
          <Notice tone="amber">No partners contacted yet. Your analyst will send the pack from Consult and status will appear here.</Notice>
        </div>
      ) : (
        <ul className={`mt-8 divide-y divide-[#fce7f3] overflow-hidden ${CARD}`} style={CARD_STYLE}>
          {rows.map((s) => {
            const st = STATUS[s.status] || { label: s.status, tone: 'slate' as const };
            const when = s.statusUpdatedAt || s.createdAt;
            return (
              <li key={s.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-[#6b0030]">{s.firm}</p>
                    {s.focus ? <p className="mt-0.5 text-xs text-[#c084a0]">{s.focus}</p> : null}
                    {s.note ? <p className="mt-2 text-sm leading-relaxed text-[#4a1a30]">{s.note}</p> : null}
                  </div>
                  <div className="shrink-0 text-right">
                    <StatusPill label={st.label} tone={st.tone} />
                    <p className="mt-1.5 text-[11px] text-[#c084a0]">{formatWhen(when)}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
